import { inject } from '@angular/core';
import { EventService, ImageConversionService } from '@core/services';
import { Observable } from 'rxjs';
import { EventEntity, EventTag, GroupEntity, UserEntity, UserEventLeaveDatesDTO } from '@core/entities';
import { map } from 'rxjs/operators';
import { instanceToPlain, plainToClass, plainToInstance } from 'class-transformer';
import { GenerateImageUrlWithToken, GetUserId, removeNullAndUndefinedProperties } from '@core/utils';
import { PermissionService } from '@auth';
import { DocumentUpdateType, SectionType } from '@core/enums';
import { UseNotification } from '@core/usecases/useNotification';

export class UseEvent {
  private readonly _imageConversionService = inject(ImageConversionService);
  private readonly _permissionService = inject(PermissionService);

  private readonly _eventService = inject(EventService);
  private readonly _useNotification = new UseNotification();

  getAllEvents(): Observable<EventEntity[]> {
    const service$ = this._permissionService.isGuest() ? this._eventService.getOpenEvents() : this._eventService.getAllEvents();

    return service$.pipe(
      map((response) => {
        if (response) {
          return response.map((item: any) => {
            const transformedItem = plainToClass(EventEntity, item);

            if (transformedItem && transformedItem.id && transformedItem.image) {
              transformedItem.imageUrl = GenerateImageUrlWithToken(transformedItem.id, SectionType.EVENT);
            }

            return transformedItem;
          });
        }
        return null;
      }),
    );
  }

  getAEvent(eventId: number): Observable<EventEntity> {
    return this._eventService.getAEvent(eventId).pipe(
      map((response: EventEntity) => {
        if (response) {
          const event = plainToClass(EventEntity, response);
          event.isMember = event.participants?.some((participant) => +participant.userId === +GetUserId());
          if (event.id && event.image) {
            event.imageUrl = GenerateImageUrlWithToken(event.id, SectionType.EVENT);
          }
          if (event.eventTask && event.eventTask.id) {
            event.isTask = true;
          }

          if (event.eventShifts && event.eventShifts.length) {
            event.isWorkShift = true;

            event.eventShifts.forEach((shift: any) => {
              shift.shiftUsers = shift.shiftAssign.map((user: any) => plainToInstance(UserEntity, user.users));
              if (shift.upload && shift.upload.length) shift.imageUrl = GenerateImageUrlWithToken(shift.id, SectionType.EVENT_SHIFT);
            });
          }

          if (event.group && event.group['group']) {
            event.group = removeNullAndUndefinedProperties(event.group['group'] ? plainToInstance(GroupEntity, event.group['group']) : plainToInstance(GroupEntity, event.group));
          }

          // Below transformation is for the update record

          if (event?.updatedRecord) {
            event.updatedRecord = plainToInstance(EventEntity, JSON.parse(event.updatedRecord as any)) as EventEntity;
            event.updatedRecord.imageUrl = event.updatedRecord.image ? GenerateImageUrlWithToken(event.id, SectionType.UPDATE_EVENT) : null;

            if (event?.updatedRecord?.eventTagsArray) {
              event.updatedRecord.eventTags = JSON.parse(event?.updatedRecord.eventTagsArray as any);
              if (event?.updatedRecord?.eventTags?.length) {
                event.updatedRecord.eventTags = event?.updatedRecord.eventTags.map(
                  (tag: any) =>
                    ({
                      tagName: tag,
                      tagId: 0,
                    }) as EventTag,
                );
              }
            }

            if (event?.updatedRecord?.eventDate && event?.updatedRecord?.eventDate.length) {
              event.updatedRecord.recurringDates = event.updatedRecord.eventDate;
            }
          }

          return event;
        }
        return null;
      }),
    );
  }

  getTeamMembers(teamId: number): Observable<UserEntity[]> {
    return this._eventService.getTeamMembers(teamId).pipe(
      map((response: any[]): UserEntity[] => {
        if (!response) {
          return [] as UserEntity[];
        }

        const members = response.map((item: any) => plainToInstance(UserEntity, item as UserEntity));

        return members;
      }),
    );
  }

  createEvent(formData: FormData): Observable<any> {
    return this._eventService.create(formData);
  }

  updateEvent(id: number, formData: FormData): Observable<any> {
    return this._eventService.update(id, formData);
  }

  hideEvent(eventId: number, hiddenStatus: 0 | 1): Observable<any> {
    const data = { id: eventId, hide_event: hiddenStatus };
    return this._eventService.hideEventSeries(data);
  }

  getHiddenEventsList(): Observable<any> {
    return this._eventService.hiddenEventsList().pipe(
      map((response) => {
        if (response) {
          return response.map((item: any) => {
            const transformedItem = plainToClass(EventEntity, item);

            if (transformedItem && transformedItem.id && transformedItem.image) {
              transformedItem.imageUrl = GenerateImageUrlWithToken(transformedItem.id, SectionType.EVENT);
            }
            return transformedItem;
          });
        }
        return null;
      }),
    );
  }

  deleteEvent(id: number): Observable<any> {
    return this._eventService.delete(id);
  }

  deleteEventFile(id: number): Observable<any> {
    return this._eventService.deleteFile(id);
  }

  markEventsAsRead(): Observable<any> {
    return this._eventService.markAsRead();
  }

  joinEvent(eventId: number, selectedDates: any): Observable<any> {
    return this._useNotification.acceptInvitedEvent(eventId, selectedDates);
  }

  leaveEvent(eventId: number, leaveDates: UserEventLeaveDatesDTO): Observable<any> {
    const userId = GetUserId();
    if (!userId) {
      return new Observable((observer) => {
        observer.error('User not logged in');
      });
    }

    if (eventId) {
      return this._eventService.leaveEvent(eventId, userId, instanceToPlain(leaveDates));
    }

    return new Observable((observer) => {
      observer.error('Invalid event ID');
    });
  }

  denyEvent(eventId: number): Observable<any> {
    return this._useNotification.denyInvitedEvent(eventId);
  }

  approveEvent(eventId: number): Observable<any> {
    return this._useNotification.approvedEvents(eventId);
  }

  approveEventUpdate(eventId: number): Observable<any> {
    return this._useNotification.approvedUpdateEvents(eventId);
  }

  unApproveEvent(eventId: number, reason: string): Observable<any> {
    return this._useNotification.unapprovedEvent(eventId, reason);
  }

  downloadDocument(eventId: number, documentName: string): Observable<any> {
    const data = { event_id: eventId, name: documentName, type: DocumentUpdateType.ATTACHMENT };
    return this._eventService.downloadDocument(data);
  }
}
